// Pieces that only the Alpine design renders: the photo cover, the two-clock strip,
// the scroll-linked altimeter and the field log carousel.
import React, {useEffect, useState} from "react";
import {Moon, Mountain, Sun} from "lucide-react";
import ApertureCarousel from "./components/ui/aperture-carousel";
import {formatFeet, photos, route} from "./FirstSFGData";

// A two-layer mountain silhouette. The front layer matches the page background,
// so the photo above appears to sink behind a ridgeline.
export const Ridge = ({className}: {className?: string}) => (
    <svg className={className} viewBox={'0 0 1440 140'} preserveAspectRatio={'none'} aria-hidden={true}>
        <path
            className={'sfg-ridge-back'}
            d={'M0 140V92l70-22 60 14 90-48 70 30 60-18 110-46 80 38 50-10 90 40 70-20 110-52 90 44 60-12 100 34 80-24 80 20 120-36 60 18v108z'}
        />
        <path
            className={'sfg-ridge-front'}
            d={'M0 140v-28l90-18 80 12 110-34 90 26 70-8 120-40 100 36 60-6 110 30 90-22 110 18 100-30 90 20 70-10 150 34v20z'}
        />
    </svg>
)

const zoneClock = (now: Date, timeZone: string) => {
    const parts = new Intl.DateTimeFormat('en-US', {timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).formatToParts(now)
    const hour = Number(parts.find(part => part.type === 'hour')?.value ?? 0)
    const minute = parts.find(part => part.type === 'minute')?.value ?? '00'
    return {hour, label: `${String(hour).padStart(2, '0')}:${minute}`, isDay: hour >= 6 && hour < 18}
}

const AlpineClocks = ({now}: {now: Date}) => {
    const jblm = zoneClock(now, 'America/Los_Angeles')
    const okinawa = zoneClock(now, 'Asia/Tokyo')
    const pacificZone = new Intl.DateTimeFormat('en-US', {timeZone: 'America/Los_Angeles', timeZoneName: 'short'})
        .formatToParts(now).find(part => part.type === 'timeZoneName')?.value ?? 'PT'
    const hoursAhead = (okinawa.hour - jblm.hour + 24) % 24
    const clocks = [
        {place: 'JBLM, Washington', zone: pacificZone, ...jblm},
        {place: 'Torii Station, Okinawa', zone: 'JST', ...okinawa},
    ]
    return (
        <div className={'sfg-alpine-clocks'}>
            <p className={'sfg-panel-label'}>One Group, two time zones</p>
            <dl>
                {clocks.map(clock => (
                    <div key={clock.place}>
                        <dt>{clock.place}</dt>
                        <dd>
                            {clock.isDay ? <Sun size={18} aria-label={'Daytime'}/> : <Moon size={18} aria-label={'Nighttime'}/>}
                            <span className={'sfg-alpine-clock-time'}>{clock.label}</span>
                            <span className={'sfg-alpine-clock-zone'}>{clock.zone}</span>
                        </dd>
                    </div>
                ))}
            </dl>
            <p className={'sfg-fine'}>Okinawa is {hoursAhead} hours ahead of JBLM right now.</p>
        </div>
    )
}

interface heroProps {
    now: Date
    nextUp: React.ReactNode
    onOpenBoard: () => void
    onOpenCeremony: () => void
}

export const AlpineHero = ({now, nextUp, onOpenBoard, onOpenCeremony}: heroProps) => (
    <section className={'sfg-hero sfg-alpine-hero'}>
        <div className={'sfg-alpine-cover'}>
            <img className={'sfg-alpine-cover-img'} src={photos.alpineHero.src} alt={photos.alpineHero.alt}/>
            <div className={'sfg-alpine-cover-shade'} aria-hidden={true}/>
            <div className={'sfg-alpine-cover-text'}>
                <p className={'sfg-eyebrow'}>
                    <Mountain size={14} aria-hidden={true}/> Field guide · Winter edition
                </p>
                <h1 className={'sfg-hero-title'}>
                    <span className={'sfg-hero-unit'}>1st Special Forces Group (Airborne)</span>
                    First Group
                </h1>
                <p className={'sfg-motto'}>De Oppresso Liber</p>
            </div>
            <Ridge className={'sfg-alpine-ridge'}/>
        </div>
        <div className={'sfg-alpine-hero-body'}>
            <div className={'sfg-alpine-intro'}>
                <p className={'sfg-hero-copy'}>
                    Announcements, ceremonies, flyers and events for the Group and its families. One place, always current.
                </p>
                <div className={'sfg-hero-actions'}>
                    <button className={'sfg-btn sfg-btn-primary'} onClick={onOpenBoard}>Open the board</button>
                    <button className={'sfg-btn sfg-btn-ghost'} onClick={onOpenCeremony}>Ceremony invite</button>
                </div>
                <p className={'sfg-alpine-route-note'}>
                    Scroll to climb. Each section is a waypoint on the Mount Rainier summit route, the peak that watches over JBLM.
                </p>
            </div>
            {nextUp}
            <AlpineClocks now={now}/>
        </div>
    </section>
)

// Turns scroll position into an elevation. Each waypoint's elevation is reached
// as its section crosses the middle of the screen, so the readout matches the section codes.
const useElevation = () => {
    const [reading, setReading] = useState({elevation: route.base.elevation, progress: 0, name: `to ${route.waypoints[0].name}`})

    useEffect(() => {
        let frame = 0
        const update = () => {
            frame = 0
            const y = window.scrollY
            const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
            const anchor = window.innerHeight * .5
            const stops = [
                {at: 0, ...route.base},
                ...route.waypoints.map(point => {
                    const element = document.getElementById(point.sectionId)
                    const at = element ? Math.max(0, element.getBoundingClientRect().top + y - anchor) : Infinity
                    return {at, name: point.name, elevation: point.elevation}
                }),
            ]
            let elevation = stops[stops.length - 1].elevation
            // Between waypoints the tag names the next one; past the last it names the summit itself.
            let name = stops[stops.length - 1].name
            for (let i = 0; i < stops.length - 1; i++) {
                const from = stops[i]
                const to = stops[i + 1]
                if (y < to.at) {
                    const span = to.at - from.at
                    const t = span > 0 ? Math.min(1, Math.max(0, (y - from.at) / span)) : 1
                    elevation = from.elevation + (to.elevation - from.elevation) * t
                    name = `to ${to.name}`
                    break
                }
            }
            setReading({elevation: Math.round(elevation), progress: Math.min(1, y / maxScroll), name})
        }
        const schedule = () => {
            if (!frame) frame = window.requestAnimationFrame(update)
        }
        update()
        window.addEventListener('scroll', schedule, {passive: true})
        window.addEventListener('resize', schedule)
        return () => {
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
            window.cancelAnimationFrame(frame)
        }
    }, [])

    return reading
}

// Rides the bottom edge of the sticky nav. Decorative: the section codes carry the same information.
export const AlpineAltimeter = () => {
    const {elevation, progress, name} = useElevation()
    return (
        <div className={'sfg-altimeter'} aria-hidden={true} style={{'--sfg-climb': progress} as React.CSSProperties}>
            <span className={'sfg-altimeter-fill'}/>
            <span className={'sfg-altimeter-tag'}>
                <Mountain size={12}/>
                <span className={'sfg-altimeter-feet'}>{formatFeet(elevation)}</span>
                <span className={'sfg-altimeter-name'}>{name}</span>
            </span>
        </div>
    )
}

export const AlpineFieldLog = () => (
    <section className={'sfg-alpine-log'} aria-label={'Field log'}>
        <div className={'sfg-alpine-log-head'}>
            <p className={'sfg-section-code'}>Field log</p>
            <h2>Out there</h2>
            <p className={'sfg-section-copy'}>Tap the right side for the next photo, or swipe. Photos are placeholders for the Group's own.</p>
        </div>
        <ApertureCarousel
            className={'sfg-alpine-carousel'}
            slides={photos.fieldLog}
            height={'clamp(440px, 78svh, 820px)'}
            ariaLabel={'Field log photos'}
        />
    </section>
)
